import { jsx, jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { SubmitButton } from "../shared/submit-button.js";
import { useTranslation } from "react-i18next";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { DifficultyBadge, DebugQuizId } from "../shared/debug-quiz-id.js";
import { QUIZ_INLINE_STYLES, QUIZ_CHIP_STYLES } from "../shared/quiz-styles.js";
import { formatProblem } from "../../../utils/quiz-parser.js";
import { useDragTextQuiz } from "../hooks/use-drag-text-quiz.js";
import XCircle from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/x-circle.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
function DragTextQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel
}) {
  var _a;
  const { t } = useTranslation("quiz");
  const {
    answers,
    currentIndex,
    blankCount,
    problemKey,
    availableOptions,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolveLabel,
    handleBlankClick,
    handleClearBlank,
    handleSelectOption,
    handleSubmit
  } = useDragTextQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const renderedProblem = useMemo(() => {
    if (answers.length === 0 && blankCount > 0) return [];
    const timestamp = problemKey;
    return formatProblem(
      quiz.problem,
      `${quizId}-problem-${timestamp}`,
      (index) => {
        var _a2, _b;
        const value = answers[index] || "";
        const valueLabel = resolveLabel(value);
        const isActive = currentIndex === index && !isSolved && !isViewSolved;
        const hasValue = value !== "";
        const isCorrect = isSolved ? (_a2 = solveResult == null ? void 0 : solveResult.corrects) == null ? void 0 : _a2[index] : null;
        const correctAnswer = ((_b = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _b[index]) ?? "";
        const correctAnswerLabel = resolveLabel(correctAnswer);
        return /* @__PURE__ */ jsx(
          "span",
          {
            onClick: () => handleBlankClick(index),
            className: cn(
              QUIZ_INLINE_STYLES.base,
              QUIZ_INLINE_STYLES.box,
              "border transition-all duration-200 min-w-0",
              // 기본 상태 (빈 박스)
              !isSolved && !isActive && !hasValue && "border-gray-300",
              !isSolved && !isActive && !hasValue && QUIZ_INLINE_STYLES.boxEmpty,
              // 활성 상태
              isActive && !hasValue && "border-blue-500 ring-2 ring-blue-100",
              // 값이 있는 상태 (filled)
              !isSolved && hasValue && QUIZ_INLINE_STYLES.boxFilled,
              // 정답
              isSolved && isCorrect === true && QUIZ_INLINE_STYLES.correct,
              // 오답 (정답보기 모드가 아닐 때만 빨강)
              isSolved && isCorrect === false && !shouldShowCorrectAnswers && QUIZ_INLINE_STYLES.wrong,
              // 정답 표시 모드 (오답인 항목을 정답으로 표시 - 초록색)
              shouldShowCorrectAnswers && isSolved && isCorrect === false && QUIZ_INLINE_STYLES.correct,
              (isSolved || isViewSolved) && "cursor-not-allowed"
            ),
            children: shouldShowCorrectAnswers && isSolved && isCorrect === false ? /* @__PURE__ */ jsx(KatexText, { text: correctAnswerLabel }) : hasValue ? /* @__PURE__ */ jsxs("span", { className: "flex gap-1 items-center", children: [
              /* @__PURE__ */ jsx(KatexText, { text: valueLabel }),
              !isSolved && !isViewSolved && /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: (e) => {
                    e.stopPropagation();
                    handleClearBlank(index);
                  },
                  className: "ml-1 p-0.5 rounded-xs hover:bg-blue-200 transition-colors",
                  children: /* @__PURE__ */ jsx(XCircle, { className: "w-3.5 h-3.5 text-gray-400 hover:text-red-400" })
                }
              )
            ] }) : /* @__PURE__ */ jsx("span", { className: QUIZ_INLINE_STYLES.boxEmpty, children: t("types.dragText.blank") })
          },
          `blank-${quizId}-${timestamp}-${index}`
        );
      },
      quiz.additionalImages ?? void 0
    );
  }, [answers, currentIndex, isSolved, solveResult, shouldShowCorrectAnswers, quiz.problem, quizId, isViewSolved, blankCount, handleBlankClick, handleClearBlank, problemKey, quiz.additionalImages, resolveLabel]);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center", children: [
            /* @__PURE__ */ jsx("div", { className: "inline-flex gap-2 items-center px-3 py-1 text-sm font-bold text-white bg-blue-500 rounded-lg", children: /* @__PURE__ */ jsx("span", { children: t("types.dragText.label") }) }),
            /* @__PURE__ */ jsx(DifficultyBadge, { level: difficultyLevel }),
            /* @__PURE__ */ jsx(DebugQuizId, { quizId })
          ] }),
          /* @__PURE__ */ jsx("h3", { className: "text-2xl font-bold leading-relaxed text-gray-900", children: /* @__PURE__ */ jsx(KatexText, { text: quiz.title }) }),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: 0.1 },
        className: cn(
          "p-4 rounded-xl bg-gray-100",
          "text-xl text-gray-700",
          "[&_.formatProblem]:leading-[1.8]",
          '[&_span[class*="inline-flex"]]:leading-normal'
        ),
        children: renderedProblem
      }
    ),
    !isSolved && !isViewSolved && availableOptions.length > 0 && /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { delay: 0.2 },
        className: "space-y-3",
        children: [
          /* @__PURE__ */ jsx("p", { className: "text-base font-semibold text-gray-600", children: t("types.dragText.selectFromChoices") }),
          /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-2 text-lg", children: availableOptions.map((option, idx) => {
            return /* @__PURE__ */ jsx(
              motion.button,
              {
                initial: { opacity: 0, scale: 0.8 },
                animate: { opacity: 1, scale: 1 },
                transition: { delay: idx * 0.05 },
                onClick: () => handleSelectOption(option.key),
                className: cn(
                  QUIZ_CHIP_STYLES.base,
                  QUIZ_CHIP_STYLES.hover
                ),
                children: /* @__PURE__ */ jsx(KatexText, { text: option.label })
              },
              option.key || `option-${idx}`
            );
          }) })
        ]
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers ? (_a = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _a.map((a) => resolveLabel(a)).join(", ") : void 0,
        disableSound
      }
    ),
    isSolved && (solveResult == null ? void 0 : solveResult.description) && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: solveResult.description,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !solveResult.correct,
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  DragTextQuizAdult
};
//# sourceMappingURL=drag-text-adult.js.map
